import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export type NodeType = 'REGION' | 'AREA' | 'BRANCH' | 'TEAM' | 'AGENT'

export interface OrgNode {
  nodeId: string
  parentId: string | null
  nodeType: NodeType
  code: string
  name: string
  managerUserId: string | null
  agentUserId: string | null
  lft: number
  rgt: number
  status: 'ACTIVE' | 'ARCHIVED'
}

export function useOrgTree() {
  return useQuery({
    queryKey: ['admin', 'org', 'tree'],
    queryFn: async () => {
      const res = await api.get<OrgNode[]>('/v1/org/tree')
      return res.data
    },
  })
}

export interface CreateNonAgentRequest {
  parentId: string | null
  nodeType: Exclude<NodeType, 'AGENT'>
  code: string
  name: string
  managerUserId: string | null
}

export function useCreateOrgNode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateNonAgentRequest) => {
      const res = await api.post<OrgNode>('/v1/org/nodes', body)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'org'] }),
  })
}

export interface PlaceAgentRequest {
  parentId: string
  code: string
  name: string
  agentUserId: string
}

export function usePlaceAgent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ parentId, ...body }: PlaceAgentRequest) => {
      const res = await api.post<OrgNode>(`/v1/org/nodes/${parentId}/agents`, body)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'org'] }),
  })
}

export interface UpdateNodeRequest {
  nodeId: string
  name?: string
  managerUserId?: string | null
  clearManager?: boolean
}

export function useUpdateOrgNode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ nodeId, ...body }: UpdateNodeRequest) => {
      const res = await api.patch<OrgNode>(`/v1/org/nodes/${nodeId}`, body)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'org'] }),
  })
}

export function useArchiveOrgNode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (nodeId: string) => {
      await api.delete(`/v1/org/nodes/${nodeId}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'org'] }),
  })
}
